/**
 * steps/RiskIntroduction.tsx
 * Risk management introduction step for the onboarding wizard
 *
 * Educational screen that introduces users to risk management concepts
 * before they configure their risk settings. Explains percentage vs fixed
 * risk and the cascade system (Account → Strategy → Playbook).
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useCallback } from 'react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, CardFooter, Button } from '@enfomentis/ui';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Custom hook for intersection observer
 */
const useIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: Partial<{
    threshold: number;
    root: Element | null;
    rootMargin: string;
  }>
) => {
  const [elements, setElements] = useState<Element[]>([]);

  useEffect(() => {
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(callback, {
      threshold: 0.3,
      ...options
    });

    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [elements, callback, options]);

  const addElement = useCallback((element: Element | null) => {
    if (element && !elements.includes(element)) {
      setElements(prev => [...prev, element]);
    }
  }, [elements]);

  return addElement;
};

/**
 * Risk Introduction component props
 */
interface RiskIntroductionProps {
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

export const RiskIntroduction: React.FC<RiskIntroductionProps> = ({ goToNextStep, goToPreviousStep }) => {
  const { markCompleted } = useOnboardingData();

  // State for scroll-based engagement
  const [revealedSections, setRevealedSections] = useState<Set<string>>(new Set()); // Start empty - reveal as user scrolls
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Intersection observer callback
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const sectionId = entry.target.getAttribute('data-section-id');
        if (sectionId) {
          setRevealedSections(prev => new Set([...prev, sectionId]));
        }
      }
    });
  }, []);

  // Set up intersection observer
  const addToObserver = useIntersectionObserver(handleIntersection);

  // Show intro section immediately on mount
  useEffect(() => {
    setRevealedSections(prev => new Set([...prev, 'intro']));
  }, []);

  // Handler for "Learn More" - scrolls to second card
  const handleLearnMore = useCallback(() => {
    const secondCard = scrollContainerRef.current?.querySelector('[data-section-id="risk-per-trade"]');
    secondCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Handler for continue button - marks complete and goes to next step
  const handleContinue = useCallback(() => {
    markCompleted('risk-intro');
    goToNextStep();
  }, [markCompleted, goToNextStep]);

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0"
    >
      <CardBody className="flex-1 p-0">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-y-auto px-6 scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-0 scrollbar-thin scrollbar-thumb-default-300 scrollbar-track-transparent"
          style={{
            scrollSnapType: 'y proximity',
            scrollBehavior: 'smooth'
          }}
        >

          {/* Card 1: Introduction to Risk Management */}
          <div
            data-section-id="intro"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 ${
              revealedSections.has('intro')
                ? 'opacity-100 translate-y-0'
                : 'opacity-100 translate-y-0'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Introduction to Risk Management
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <div className="space-y-4 text-base leading-relaxed text-text-secondary pt-2">
                  <div>
                    In the next stage, you'll configure how much you're willing to <span className="text-primary font-semibold">risk per trade</span>.
                  </div>
                  <div>
                    Proper risk management protects your capital and helps you trade with confidence, knowing exactly what you're risking before you enter each position.
                  </div>
                </div>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6 flex flex-col gap-4">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={handleLearnMore}
                >
                  LEARN MORE
                </Button>
                <div className="flex justify-between items-center w-full">
                  <Button
                    variant="light"
                    onPress={goToPreviousStep}
                    startContent={<span>←</span>}
                    className="flex-1 justify-start"
                  >
                    Back
                  </Button>
                  <Button
                    variant="light"
                    onPress={handleContinue}
                    endContent={<span>→</span>}
                    className="flex-1 justify-end"
                  >
                    Skip Education
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 2: What is Risk Per Trade? */}
          <div
            data-section-id="risk-per-trade"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-100 ${
              revealedSections.has('risk-per-trade')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                What is Risk Per Trade?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Risk per trade defines the maximum amount you're willing to lose on a single position.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  This is calculated by multiplying your price risk by your position size and contract specifications.
                </p>
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-semibold text-text-primary">Example:</p>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardBody className="space-y-2">
                      <div className="text-sm leading-relaxed text-text-secondary">
                        <span className="font-semibold text-text-primary">Position:</span> 10 Contracts Long @ $50.00
                      </div>
                      <div className="text-sm leading-relaxed text-text-secondary">
                        <span className="font-semibold text-text-primary">Stop Loss:</span> $49.50
                      </div>
                      <div className="text-sm leading-relaxed text-text-secondary">
                        <span className="font-semibold text-text-primary">Contract:</span> Represents 10 shares
                      </div>
                      <div className="text-sm leading-relaxed text-text-primary font-semibold mt-3 pt-3 border-t-1">
                        ($50 - $49.50) × 10 Contracts × 10 Shares/Contract = <span className="text-primary">$50.00 total risk</span>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                By defining risk upfront, you protect your account from catastrophic losses.
              </CardFooter>
            </Card>
          </div>

          {/* Card 3: Two Ways to Define Risk */}
          <div
            data-section-id="two-ways"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-200 ${
              revealedSections.has('two-ways')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Two Ways to Define Risk
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  You can express risk in two ways. Here's how each affects your account over time:
                </p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardHeader className="text-base font-semibold border-b-1">
                      1% Percentage Risk
                    </CardHeader>
                    <CardBody className="pt-3 font-mono text-xs space-y-1">
                      <div className="text-text-secondary">
                        Balance: $10,000 → Risk: $100 → <span className="text-success">Win +$300</span>
                      </div>
                      <div className="text-text-secondary">
                        Balance: $10,300 → Risk: $103 → <span className="text-danger">Loss -$103</span>
                      </div>
                      <div className="text-text-secondary">
                        Balance: $10,197 → Risk: $102 → <span className="text-success">Win +$306</span>
                      </div>
                      <div className="text-text-primary font-semibold pt-2 border-t-1">
                        Final: $10,503
                      </div>
                    </CardBody>
                  </Card>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardHeader className="text-base font-semibold border-b-1">
                      $100 Fixed Risk
                    </CardHeader>
                    <CardBody className="pt-3 font-mono text-xs space-y-1">
                      <div className="text-text-secondary">
                        Balance: $10,000 → Risk: $100 → <span className="text-success">Win +$300</span>
                      </div>
                      <div className="text-text-secondary">
                        Balance: $10,300 → Risk: $100 → <span className="text-danger">Loss -$100</span>
                      </div>
                      <div className="text-text-secondary">
                        Balance: $10,200 → Risk: $100 → <span className="text-success">Win +$300</span>
                      </div>
                      <div className="text-text-primary font-semibold pt-2 border-t-1">
                        Final: $10,500
                      </div>
                    </CardBody>
                  </Card>
                </div>
                <p className="text-sm leading-relaxed text-text-secondary mt-4 italic">
                  Percentage risk scales with your account, while fixed risk stays constant.
                </p>
              </CardBody>
            </Card>
          </div>

          {/* Card 4: The Risk Cascade System */}
          <div
            data-section-id="cascade"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-300 ${
              revealedSections.has('cascade')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                The Risk Cascade System
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Risk settings follow a cascade from general to specific.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  This allows you to set a default risk, then override it for specific strategies or playbooks when needed.
                </p>
                <div className="mt-6">
                  <Card radius="sm" shadow="sm" className="bg-default-100 font-mono">
                    <CardBody className="space-y-1">
                      <div className="text-text-primary font-semibold">Account Default Risk</div>
                      <div className="ml-4 text-text-secondary text-sm">↳ Your fallback for all trades</div>
                      <div className="ml-2 text-text-primary font-semibold mt-3">Strategy Risk (optional)</div>
                      <div className="ml-6 text-text-secondary text-sm">↳ Override for specific strategy</div>
                      <div className="ml-4 text-text-primary font-semibold mt-3">Playbook Risk (optional)</div>
                      <div className="ml-8 text-text-secondary text-sm">↳ Most specific, takes precedence</div>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                The system uses the most specific risk setting available, cascading up if no override exists.
              </CardFooter>
            </Card>
          </div>

          {/* Card 5: How the Cascade Works - Example */}
          <div
            data-section-id="cascade-example"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-400 ${
              revealedSections.has('cascade-example')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                How the Cascade Works - Example
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Here's how the cascade resolves for different combinations:
                </p>
                <div className="mt-6">
                  <Card radius="sm" shadow="sm" className="bg-default-100 font-mono">
                    <CardHeader className="text-sm font-semibold border-b-1">
                      <div>Account Default Risk: <span className="text-primary">1% per trade</span></div>
                    </CardHeader>
                    <CardBody className="space-y-4 pt-4">
                      <div>
                        <div className="text-text-primary font-semibold">Strategy A (No Override)</div>
                        <div className="ml-2 text-text-secondary text-xs md:text-sm mt-1">├─ Playbook A (No Override) → <span className="text-primary">Uses 1%</span></div>
                        <div className="ml-2 text-text-secondary text-xs md:text-sm">└─ Playbook B (<span className="text-secondary font-semibold">0.5% Override</span>) → <span className="text-secondary font-semibold">Uses 0.5%</span></div>
                      </div>
                      <div>
                        <div className="text-text-primary font-semibold">Strategy B (<span className="text-warning font-semibold">2% Override</span>)</div>
                        <div className="ml-2 text-text-secondary text-xs md:text-sm mt-1">├─ Playbook A (No Override) → <span className="text-warning font-semibold">Uses 2%</span></div>
                        <div className="ml-2 text-text-secondary text-xs md:text-sm">├─ Playbook B (No Override) → <span className="text-warning font-semibold">Uses 2%</span></div>
                        <div className="ml-2 text-text-secondary text-xs md:text-sm">└─ Playbook C (<span className="text-secondary font-semibold">3% Override</span>) → <span className="text-secondary font-semibold">Uses 3%</span></div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                The system always uses the most specific risk setting, giving you flexibility while maintaining clear defaults.
              </CardFooter>
            </Card>
          </div>

          {/* Card 6: Multiple Risk Profiles */}
          <div
            data-section-id="profiles"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-500 ${
              revealedSections.has('profiles')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Multiple Risk Profiles
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  You can create multiple risk profiles for different market conditions. Each profile defines risk across your entire trading hierarchy.
                </p>
                <div className="mt-6">
                  <Card radius="sm" shadow="sm" className="bg-default-100 font-mono">
                    <CardHeader className="text-sm font-semibold border-b-1">
                      Example: Grid Configuration
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b-1">
                              <th className="text-left pb-2 pr-3 font-semibold text-text-primary">Level</th>
                              <th className="text-center pb-2 px-2 font-semibold text-secondary">Conservative</th>
                              <th className="text-center pb-2 px-2 font-semibold text-primary">Standard</th>
                              <th className="text-center pb-2 px-2 font-semibold text-warning">A+</th>
                            </tr>
                          </thead>
                          <tbody className="text-text-secondary">
                            <tr className="border-b-1">
                              <td className="py-2 pr-3">Account Default</td>
                              <td className="text-center py-2 px-2">1%</td>
                              <td className="text-center py-2 px-2">1%</td>
                              <td className="text-center py-2 px-2">1%</td>
                            </tr>
                            <tr className="border-b-1">
                              <td className="py-2 pr-3">Scalping Strategy</td>
                              <td className="text-center py-2 px-2 text-secondary font-semibold">0.5%</td>
                              <td className="text-center py-2 px-2 text-primary font-semibold">1%</td>
                              <td className="text-center py-2 px-2 text-warning font-semibold">1.5%</td>
                            </tr>
                            <tr className="border-b-1">
                              <td className="py-2 pr-3 pl-3">↳ Pullback Entry</td>
                              <td className="text-center py-2 px-2 text-secondary font-semibold">0.75%</td>
                              <td className="text-center py-2 px-2 text-primary font-semibold">1.5%</td>
                              <td className="text-center py-2 px-2 text-warning font-semibold">2.5%</td>
                            </tr>
                            <tr>
                              <td className="py-2 pr-3">Swing Strategy</td>
                              <td className="text-center py-2 px-2" title="Uses account default">—</td>
                              <td className="text-center py-2 px-2" title="Uses account default">—</td>
                              <td className="text-center py-2 px-2" title="Uses account default">—</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="text-text-secondary text-xs mt-4 pt-3 border-t-1 italic">
                        "—" means use account default. Each column is a complete profile you select during trade entry.
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                <div>During trade entry, you <span className="text-primary font-semibold">pick which profile to use based on market conditions </span> (e.g., negative confluences, entry quality).</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 7: Ready to Configure Your Risk? */}
          <div
            data-section-id="ready"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-600 ${
              revealedSections.has('ready')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Ready to Configure Your Risk?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  In the next step, you'll configure your risk profiles:
                </p>
                <div className="mt-6">
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardBody className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-text-primary mb-2">Start with one profile:</p>
                        <div className="ml-3 space-y-1 text-sm text-text-secondary">
                          <div>• Set your account default risk (e.g., 1%)</div>
                          <div>• Optionally configure overrides for specific strategies/playbooks</div>
                        </div>
                      </div>
                      <div className="border-t-1 pt-3">
                        <p className="text-sm font-semibold text-text-primary mb-2">Add more profiles if desired:</p>
                        <div className="ml-3 space-y-1 text-sm text-text-secondary">
                          <div>• Create "A+" profile for perfect setups</div>
                          <div>• Create "Conservative" for riskier setups</div>
                          <div>• Configure each profile's complete cascade</div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
                <p className="text-text-secondary text-base leading-relaxed mt-6 italic">
                  You can always keep it simple with one profile and add more later as your trading evolves.
                </p>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={handleContinue}
                >
                  Continue to Risk Configuration
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
